import { useEffect, useState, useCallback, useRef } from 'react'
import { Modal } from '@/components/common/Modal'
import { Button } from '@/components/common/Button'
import { ImportRunDialog } from './ImportRunDialog'
import { isImportCancelled, timetableImportV2Api } from '@/services/api/timetableImportV2'
import { extractErrorMessage } from '@/utils/errorHandler'
import { Loader2, CheckCircle2, AlertCircle, FileText, ArrowRight, AlertTriangle } from 'lucide-react'
import { TeacherAssignmentDialog } from './TeacherAssignmentDialog'
import { ValidityDialog } from './ValidityDialog'
import { TERMINAL_BATCH_STATUSES } from './constants'
import { importIssueText, importRunLabel, pendingTeachersForDialog } from './continueBatch'

interface BatchStatusViewProps {
  batchId: string
  onClose: () => void
  onImportAll: () => void
  onOpenTokenMatching: (sessionId: string) => void
}

interface BatchSection {
  section_index: number
  section_identifier: string | null
  section_type: string | null
  section_pages: number[]
  session_id: string | null
  status: string
  error: string | null
  has_unmatched: boolean | null
  matched_count: number | null
  unmatched_count: number | null
  complete_entries: number
  total_entries: number
}

/** Maximum duration to poll before giving up (5 minutes). */
const MAX_POLL_DURATION_MS = 5 * 60 * 1000

const statusLabels: Record<string, string> = {
  pending_resolution: 'Auflösung nötig',
  incomplete: 'Unvollständig',
  resolved: 'Aufgelöst',
  imported: 'Importiert',
  failed: 'Fehler',
  cancelled: 'Abgebrochen',
  pending_assignment: 'Zuordnung nötig',
}

const statusIcons: Record<string, React.ReactNode> = {
  pending_resolution: <AlertTriangle className="h-4 w-4 text-yellow-500" />,
  incomplete: <AlertTriangle className="h-4 w-4 text-orange-500" />,
  resolved: <CheckCircle2 className="h-4 w-4 text-green-500" />,
  imported: <CheckCircle2 className="h-4 w-4 text-blue-500" />,
  failed: <AlertCircle className="h-4 w-4 text-red-500" />,
  cancelled: <AlertCircle className="h-4 w-4 text-gray-400" />,
  pending_assignment: <AlertTriangle className="h-4 w-4 text-orange-500" />,
}

export function BatchStatusView({ batchId, onClose, onImportAll, onOpenTokenMatching }: BatchStatusViewProps) {
  const [batchStatus, setBatchStatus] = useState<string>('extracting')
  const [sections, setSections] = useState<BatchSection[]>([])
  const [totalSections, setTotalSections] = useState(0)
  const [loading, setLoading] = useState(true)
  const [importing, setImporting] = useState(false)
  const [importError, setImportError] = useState<string | null>(null)
  const [importProgress, setImportProgress] = useState(0)
  const [importPages, setImportPages] = useState({ done: 0, total: 0 })
  const [importStatus, setImportStatus] = useState('Import läuft…')
  const [stale, setStale] = useState(false)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const startTimeRef = useRef<number>(0)
  // Orphaned-fix-v2: Pending teacher assignment state
  const [pendingTeachers, setPendingTeachers] = useState<string[]>([])
  const [perTeacherCounts, setPerTeacherCounts] = useState<Record<string, number>>({})
  const [perTeacherSessions, setPerTeacherSessions] = useState<Record<string, { session_id: string; section_pages: number[]; section_identifier: string | null }>>({})
  const [showAssignmentDialog, setShowAssignmentDialog] = useState(false)
  const [assignmentDismissed, setAssignmentDismissed] = useState(false)
  // Timetable-validity-v1: Validity dialog state
  const [showValidityDialog, setShowValidityDialog] = useState(false)
  const [assignedTeachers, setAssignedTeachers] = useState<string[]>([])
  const [extractionMetadata, setExtractionMetadata] = useState<Record<string, any> | null>(null)

  const fetchStatus = useCallback(async () => {
    try {
      const data = await timetableImportV2Api.getBatchStatus(batchId)
      startTimeRef.current = Date.now()
      setBatchStatus(data.status)
      setSections((data.sections || []) as BatchSection[])
      setTotalSections(data.total_sections)
      setLoading(false)

      if (TERMINAL_BATCH_STATUSES.includes(data.status) || data.paused_at_section !== null) {
        if (intervalRef.current) {
          clearInterval(intervalRef.current)
          intervalRef.current = null
        }
      }

      // Orphaned-fix-v2: Check for pending_assignment status
      if (data.status === 'pending_assignment' && !assignmentDismissed) {
        try {
          const pendingData = await timetableImportV2Api.getBatchPendingStatus(batchId)
          setPendingTeachers(pendingData.pending_teachers || [])
          setPerTeacherCounts(pendingData.per_teacher_counts || {})
          setPerTeacherSessions(pendingData.per_teacher_sessions || {})
          setShowAssignmentDialog(true)
        } catch (err) {
          console.error('Failed to get pending status:', err)
        }
      }

      // Store extraction_metadata for validity check
      if (data.extraction_metadata) {
        setExtractionMetadata(data.extraction_metadata)
      }
    } catch {
      if (Date.now() - startTimeRef.current > MAX_POLL_DURATION_MS) {
        setStale(true)
        if (intervalRef.current) {
          clearInterval(intervalRef.current)
          intervalRef.current = null
        }
      }
    }
  }, [batchId])

  useEffect(() => {
    startTimeRef.current = Date.now()
    fetchStatus()
    intervalRef.current = setInterval(fetchStatus, 3000)
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [fetchStatus])

  const importAbortRef = useRef<AbortController | null>(null)
  const [importCancelling, setImportCancelling] = useState(false)
  const cancelImport = () => {
    const controller = importAbortRef.current
    if (!controller || controller.signal.aborted) return
    setImportCancelling(true)
    setImportStatus('Wird abgebrochen…')
    controller.abort()
  }

  const handleImportAll = async () => {
    if (importing) return
    const controller = new AbortController()
    importAbortRef.current = controller
    setImportCancelling(false)
    setImporting(true)
    setImportError(null)
    setImportProgress(0)
    setImportPages({ done: 0, total: 0 })
    setImportStatus('Import läuft…')
    try {
      const result = await timetableImportV2Api.importAllSections(batchId, 'resolved_only', (update) => {
        setImportProgress(update.progress || 0)
        setImportPages({ done: update.pagesDone, total: update.pagesTotal })
        setImportStatus(importRunLabel(update.message))
      }, controller.signal)
      if (controller.signal.aborted) return

      const pendingData = await timetableImportV2Api.getBatchPendingStatus(batchId)
      if (controller.signal.aborted) return
      const teachers = pendingTeachersForDialog(result.pending_teachers, pendingData)

      if (teachers.length > 0) {
        if ((result.errors || []).some((item) => item.error) || result.failed_sections) {
          setImportError(importIssueText(result))
        }
        setPendingTeachers(teachers)
        setPerTeacherCounts(pendingData.per_teacher_counts || {})
        setPerTeacherSessions(pendingData.per_teacher_sessions || {})
        setShowAssignmentDialog(true)
        return
      }

      await fetchStatus()
      if (controller.signal.aborted) return

      const issue = importIssueText(result)
      if (issue) {
        setImportError(issue)
        return
      }

      onImportAll()
    } catch (err: unknown) {
      if (controller.signal.aborted || isImportCancelled(err)) return
      setImportError(extractErrorMessage(err, { loadError: 'Import fehlgeschlagen' }))
    } finally {
      if (importAbortRef.current === controller) {
        importAbortRef.current = null
        setImportCancelling(false)
        setImporting(false)
      }
    }
  }

  if (loading) {
    return (
      <Modal isOpen={true} onClose={onClose} title="Stundenpläne verarbeiten" size="lg">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
          <span className="ml-3">Verarbeitung läuft...</span>
        </div>
      </Modal>
    )
  }

  // Stale/timeout: polling ran too long without reaching a terminal state
  if (stale) {
    return (
      <Modal isOpen={true} onClose={onClose} title="Zeitüberschreitung" size="lg">
        <div className="space-y-4">
          <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <AlertTriangle className="h-6 w-6 text-amber-500 flex-shrink-0" />
            <div>
              <div className="font-medium text-amber-800">Verarbeitung dauert zu lange</div>
              <div className="text-sm text-amber-600 mt-1">
                Die Verarbeitung der Stundenpläne konnte nicht innerhalb der erwarteten Zeit abgeschlossen werden.
                Möglicherweise ist ein Fehler aufgetreten. Du kannst das Fenster schliessen und es später erneut versuchen.
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="primary" size="sm" onClick={onClose}>
              Schliessen
            </Button>
          </div>
        </div>
      </Modal>
    )
  }

  // Orphaned-fix-v2: Show TeacherAssignmentDialog if pending_assignment
  if (batchStatus === 'pending_assignment' && showAssignmentDialog && pendingTeachers.length > 0) {
    return (
      <TeacherAssignmentDialog
        batchId={batchId}
        pendingTeachers={pendingTeachers}
        perTeacherCounts={perTeacherCounts}
        perTeacherSessions={perTeacherSessions}
        onAssigned={(code) => {
          setPendingTeachers(prev => prev.filter(c => c !== code))
          setAssignedTeachers(prev => [...new Set([...prev, code])])
          setPerTeacherSessions(prev => {
            const newSessions = { ...prev }
            delete newSessions[code]
            return newSessions
          })
          // Removed fetchStatus() to prevent overwriting local state
          // Status will be refreshed by polling interval if needed
        }}
        onSkipped={(code) => {
          setPendingTeachers(prev => prev.filter(c => c !== code))
          setPerTeacherSessions(prev => {
              const newSessions = { ...prev }
              delete newSessions[code]
              return newSessions
            })
            // Removed fetchStatus() to prevent overwriting local state
            // Status will be refreshed by polling interval if needed
          }}
          onComplete={() => {
            setShowAssignmentDialog(false)
            // Timetable-validity-v1: Show validity dialog only if no validity was set in BulkImportDialog
            const hasValidity = extractionMetadata && (extractionMetadata.valid_from || extractionMetadata.valid_to)
            if (!hasValidity) {
              setShowValidityDialog(true)
            }
            fetchStatus()
          }}
          onSkipAllComplete={() => {
            setShowAssignmentDialog(false)
            // After skip all, show validity dialog same as onComplete
            const hasValidity = extractionMetadata && (extractionMetadata.valid_from || extractionMetadata.valid_to)
            if (!hasValidity) {
              setShowValidityDialog(true)
            }
            fetchStatus()
          }}
          onClose={() => {
            setShowAssignmentDialog(false)
            setAssignmentDismissed(true)
          }}
        />
    )
  }

  // Timetable-validity-v1: Show ValidityDialog after teacher assignment
  if (showValidityDialog) {
    return (
      <ValidityDialog
        isOpen={showValidityDialog}
        batchId={batchId}
        pendingTeachers={pendingTeachers}
        assignedTeachers={assignedTeachers}
        onValiditySet={async (_validFrom, _validTo, _overlapResults) => {
          setShowValidityDialog(false)
          // After validity is set, trigger import
          await handleImportAll()
        }}
onSkip={() => {
            setShowValidityDialog(false)
            handleImportAll()
          }}
      />
    )
  }

  // Categorize sections
  const completeSections = sections.filter((s): s is BatchSection =>
    s.status === 'resolved' && s.complete_entries > 0 && s.complete_entries === s.total_entries
  )
  const incompleteSections = sections.filter((s): s is BatchSection =>
    s.status === 'resolved' && s.complete_entries < s.total_entries
  )
  const pendingResolutionSections = sections.filter((s): s is BatchSection =>
    s.status === 'pending_resolution'
  )
  const pendingAssignmentSections = sections.filter((s): s is BatchSection =>
    s.status === 'pending_assignment'
  )
  const importedSections = sections.filter((s): s is BatchSection =>
    s.status === 'imported'
  )
  const failedSections = sections.filter((s): s is BatchSection =>
    s.status === 'failed'
  )
  const canImport = completeSections.length > 0
  const hasIncomplete = incompleteSections.length > 0
  const hasPendingResolution = pendingResolutionSections.length > 0
  const hasPendingAssignment = pendingAssignmentSections.length > 0
  const hasFailed = failedSections.length > 0

  return (
    <>
    <Modal isOpen={true} onClose={importing ? () => undefined : onClose} title={`Stundenpläne: ${totalSections} erkannt`} size="lg">
      <div className="space-y-4">
        {importError && (
          <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700" role="alert">
            <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span>{importError}</span>
          </div>
        )}
        <div className="space-y-3">
          <div className="grid gap-3 grid-cols-2">
            <button
              type="button"
              disabled={!canImport || importing}
              onClick={() => { if (canImport) void handleImportAll() }}
              className="bg-green-50 border border-green-200 rounded-lg p-4 text-left enabled:hover:bg-green-100 disabled:cursor-default"
            >
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                <div className="text-2xl font-bold text-green-700">{completeSections.length}</div>
              </div>
              <div className="text-sm text-green-600 mt-1">Vollständig aufgelöst</div>
              <div className="text-xs text-green-500 mt-1">
                {canImport ? 'Alle Tokens bekannt, bereit zum Import' : 'Noch nichts bereit zum Import'}
              </div>
            </button>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-yellow-600" />
                <div className="text-2xl font-bold text-yellow-700">{incompleteSections.length}</div>
              </div>
              <div className="text-sm text-yellow-600 mt-1">Unvollständig</div>
              <div className="text-xs text-yellow-500 mt-1">
                Unbekannte Tokens müssen zugeordnet werden
              </div>
            </div>
          </div>

          {/* Row 2: Issues that need attention */}
          {(hasPendingResolution || hasPendingAssignment || hasFailed) && (
            <div className={`grid gap-3 ${(hasPendingResolution && hasPendingAssignment && hasFailed) ? 'grid-cols-3' : (hasPendingResolution && hasPendingAssignment) || (hasPendingResolution && hasFailed) || (hasPendingAssignment && hasFailed) ? 'grid-cols-2' : 'grid-cols-1'}`}>
              {hasPendingResolution && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-purple-600" />
                    <div className="text-2xl font-bold text-purple-700">{pendingResolutionSections.length}</div>
                  </div>
                  <div className="text-sm text-purple-600 mt-1">Auflösung nötig</div>
                  <div className="text-xs text-purple-500 mt-1">
                    Token-Matching erforderlich
                  </div>
                </div>
              )}

              {hasPendingAssignment && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-orange-600" />
                    <div className="text-2xl font-bold text-orange-700">{pendingAssignmentSections.length}</div>
                  </div>
                  <div className="text-sm text-orange-600 mt-1">Lehrer-Zuordnung nötig</div>
                  <div className="text-xs text-orange-500 mt-1">
                    Lehrercodes müssen einem Benutzer zugeordnet werden
                  </div>
                </div>
              )}

              {hasFailed && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-5 w-5 text-red-600" />
                    <div className="text-2xl font-bold text-red-700">{failedSections.length}</div>
                  </div>
                  <div className="text-sm text-red-600 mt-1">Fehler</div>
                  <div className="text-xs text-red-500 mt-1">
                    Extraktion fehlgeschlagen
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Row 3: Already imported */}
          {importedSections.length > 0 && (
            <div className="grid gap-3 grid-cols-1">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-blue-600" />
                  <div className="text-2xl font-bold text-blue-700">{importedSections.length}</div>
                </div>
                <div className="text-sm text-blue-600 mt-1">Importiert</div>
                <div className="text-xs text-blue-500 mt-1">
                  Bereits in die Datenbank importiert
                </div>
              </div>
            </div>
          )}

          {/* Total summary line */}
          <div className="text-sm text-gray-600 text-center pt-1">
            Total: {sections.length} Stundenpläne
            {sections.length !== totalSections && ` (angezeigt: ${sections.length}, erwartet: ${totalSections})`}
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          {canImport && (
            <Button
              variant="primary"
              size="sm"
              onClick={handleImportAll}
              disabled={importing}
              className="flex-1"
            >
              {importing ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Importieren...
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  {completeSections.length} Stundenpläne importieren
                </>
              )}
            </Button>
          )}

          {hasIncomplete && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                // Open first incomplete section for token matching
                const firstIncomplete = incompleteSections[0]
                if (firstIncomplete?.session_id) {
                  onOpenTokenMatching(firstIncomplete.session_id)
                }
              }}
              className="flex-1"
            >
              <ArrowRight className="h-4 w-4 mr-2" />
              {incompleteSections.length} unvollständige öffnen
            </Button>
          )}

          {hasPendingResolution && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                // Open first pending_resolution section for token matching
                const firstPending = pendingResolutionSections[0]
                if (firstPending?.session_id) {
                  onOpenTokenMatching(firstPending.session_id)
                }
              }}
              className="flex-1"
            >
              <ArrowRight className="h-4 w-4 mr-2" />
              {pendingResolutionSections.length} zur Auflösung öffnen
            </Button>
          )}
        </div>

        {/* Sections List */}
        <div className="max-h-64 overflow-y-auto space-y-1 border rounded-lg">
          {sections.map((section, idx) => (
            <div
              // Use combination of section_index, session_id, and array index to ensure uniqueness
              key={`${section.section_index}-${section.session_id || idx}`}
              className="flex items-center gap-3 p-2 hover:bg-gray-50 border-b last:border-b-0"
            >
              {statusIcons[section.status] || <FileText className="h-4 w-4 text-gray-400" />}
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm truncate">
                  {section.section_identifier || `Seite ${section.section_pages.join(', ')}`}
                </div>
                <div className="text-xs text-gray-500">
                  Seite {section.section_pages.join(', ')}
                  {section.total_entries > 0 && ` • ${section.complete_entries}/${section.total_entries} Einträge vollständig`}
                  {section.error ? ` • ${section.error}` : ''}
                </div>
              </div>
              <div className="text-xs font-medium">
                {statusLabels[section.status] || section.status}
              </div>
            </div>
          ))}
        </div>

        {/* Close */}
        <div className="flex justify-end pt-2 border-t">
          <Button variant="secondary" size="sm" onClick={onClose} disabled={importing}>
            Schliessen
          </Button>
        </div>
      </div>
    </Modal>
    {importing && (
      <ImportRunDialog
        progress={importProgress}
        message={importStatus}
        pagesDone={importPages.done}
        pagesTotal={importPages.total}
        onCancel={cancelImport}
        cancelling={importCancelling}
      />
    )}
    </>
  )
}
